import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SearchBox, Button, Loading } from '@evertrue/et-components';
import useDebounce from 'hooks/use-debounce';
import useInfiniteScroll from 'hooks/use-infinite-scroll';
import { useEmailTemplatesProvider } from '../email-templates-context';
import TemplatesList from './templates-list';
import SearchResultsList from './search-results-list';

const EmailTemplatesLibrary = ({ openTemplateForm }) => {
  const [searchVal, setSearchVal] = useState('');
  const debouncedSearchVal = useDebounce(searchVal, 500);
  const templateListRef = useRef(null);
  const lastSearchRef = useRef('');

  const {
    templates,
    searchResults,
    totalTemplates,
    selectedTemplate,
    setSelectedTemplate,
    loading,
    handleSearch,
    resetSearch,
    fetchTemplates,
    isSearching,
    searchTerm,
  } = useEmailTemplatesProvider();

  const handleCreateNewTemplate = () => {
    setSelectedTemplate({});
    openTemplateForm();
  };

  // Restore scroll position when component mounts or templates load
  useEffect(() => {
    if (templates.length > 0 && templateListRef.current) {
      const savedScrollPosition = sessionStorage.getItem('templateListScrollPosition');
      if (savedScrollPosition) {
        templateListRef.current.scrollTop = parseInt(savedScrollPosition, 10);
      }
    }
  }, [templates.length]);

  // Save scroll position when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (templateListRef.current) {
        sessionStorage.setItem('templateListScrollPosition', templateListRef.current.scrollTop);
      }
    };

    const listEl = templateListRef.current;
    if (listEl) {
      listEl.addEventListener('scroll', handleScroll);

      // Save final position when component unmounts
      return () => {
        // Save current scroll position before unmounting
        if (listEl.scrollTop > 0) {
          sessionStorage.setItem('templateListScrollPosition', listEl.scrollTop);
        }
        listEl.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Use the useInfiniteScroll hook for pagination
  const [loadingMore, resetOffset] = useInfiniteScroll(
    templateListRef,
    async (offset) => {
      if (!searchTerm) {
        return await fetchTemplates(offset);
      }
      return Promise.resolve();
    },
    totalTemplates,
    50 // Using the same LIMIT value from the context
  );

  // Handle search input changes
  const handleSearchChange = useCallback((query) => {
    setSearchVal(query);
  }, []);

  // Handle search when debounced search value changes - SIMPLIFIED
  useEffect(() => {
    const trimmedSearch = debouncedSearchVal?.trim() || '';

    // Don't repeat the same search
    if (trimmedSearch === lastSearchRef.current) {
      return;
    }

    if (trimmedSearch) {
      // We have a search term
      handleSearch(trimmedSearch);
      lastSearchRef.current = trimmedSearch;
    } else {
      // Search was cleared
      if (lastSearchRef.current) {
        resetSearch();
        resetOffset();
        lastSearchRef.current = '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchVal]); // Only debouncedSearchVal as dependency

  // Determine if we should show the loading spinner
  const shouldShowLoading = loading && isSearching && !loadingMore;

  return (
    <div className="templates-library--container" style={{ display: 'flex', flexDirection: 'column', height: '600px' }}>
      <SearchBox
        value={searchVal}
        onChange={handleSearchChange}
        placeholder="Search templates by name..."
        style={{ marginRight: '15px', marginBottom: '10px' }}
      />
      <div
        className="template-library--list"
        ref={templateListRef}
        style={{
          flex: 1,
          minHeight: '400px',
          maxHeight: '500px',
          overflowY: 'scroll',
          position: 'relative',
          border: '1px solid #eee',
          padding: '0 10px',
          marginBottom: '10px',
        }}
      >
        {shouldShowLoading ? (
          <div style={{ position: 'relative', paddingTop: '350px' }}>
            <Loading />
          </div>
        ) : searchTerm && searchTerm.trim() !== '' ? (
          // Search Results View
          <SearchResultsList
            searchResults={searchResults}
            searchTerm={searchTerm}
            selectedTemplate={selectedTemplate}
            openTemplateForm={openTemplateForm}
          />
        ) : (
          <>
            <TemplatesList
              templates={templates}
              openTemplateForm={openTemplateForm}
              selectedTemplate={selectedTemplate}
            />
            {loadingMore && (
              <div style={{ padding: '15px', textAlign: 'center' }}>
                <Loading size="small" />
              </div>
            )}
          </>
        )}
      </div>
      <Button onClick={handleCreateNewTemplate} type="secondary" className="create-template-btn">
        Create Template
      </Button>
    </div>
  );
};

EmailTemplatesLibrary.propTypes = {
  openTemplateForm: PropTypes.func,
};

export default EmailTemplatesLibrary;
